import { Link } from "react-router-dom";
import { Heart, Lock, ShieldOff, AlertTriangle, Users, Flag, Ban, CheckCircle2, ArrowLeft, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const MASTER = [
  { icon: Heart, color: "#E8621A", title: "Be Kind", body: "Encourage, don't criticize. No degrading other members or their posts." },
  { icon: Lock, color: "#2A9D8F", title: "Protect Privacy", body: "Don't share personal financial details or anyone else's private information." },
  { icon: ShieldOff, color: "#E76F51", title: "No Spam or Advertising", body: "No promoting products, services, or businesses for personal gain." },
  { icon: AlertTriangle, color: "#F4A261", title: "No Misinformation", body: "Only share tips you know to be accurate and legal." },
  { icon: Users, color: "#8E44AD", title: "Credit Others", body: "If you learned a tip from someone else, give them credit." },
  { icon: Flag, color: "#1B2A4A", title: "Report Violations", body: "Help keep the community safe by reporting inappropriate content." },
];

const APP_SPECIFIC = [
  { icon: Ban, color: "#E76F51", title: "No Political Posts", body: "This community is focused on practical solutions to rising costs. Political discussions belong elsewhere." },
  { icon: CheckCircle2, color: "#2A9D8F", title: "Keep Tips Practical", body: "Share real, actionable advice related to saving money or managing costs." },
];

export default function Guidelines({ standalone = false }) {
  const { user } = useAuth();

  const Wrap = standalone ? "div" : "div";
  return (
    <div className={standalone ? "min-h-screen bg-[#F7F5F2] px-4 py-8 sm:py-14" : ""}>
      <Wrap className={standalone ? "max-w-3xl mx-auto" : ""}>
        {standalone && (
          <Link
            to={user ? "/" : "/login"}
            data-testid="guidelines-back"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1B2A4A]/70 hover:text-[#E8621A] transition-colors duration-200 mb-6"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
        )}

        <div className="rounded-3xl bg-white border border-[#1B2A4A]/8 p-6 sm:p-10">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#E8621A]/10 text-[#E8621A] px-3 py-1 text-xs font-semibold tracking-widest uppercase">
            <Sparkles className="w-3 h-3" /> Community Guidelines
          </div>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-bold text-[#1B2A4A] leading-tight">
            How we <span className="text-[#E8621A]">show up</span> for each other.
          </h1>
          <p className="mt-4 text-[#1B2A4A]/70 max-w-2xl leading-relaxed">
            Surviving Together works because neighbors treat each other with kindness and honesty.
            These simple guidelines keep the space warm, useful, and safe — for everyone.
          </p>

          <section className="mt-10">
            <h2 className="font-heading text-lg font-bold text-[#1B2A4A] uppercase tracking-widest text-[#2A9D8F]">
              For everyone, everywhere
            </h2>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {MASTER.map((g, i) => (
                <div key={g.title} data-testid={`guideline-master-${i + 1}`} className="rounded-2xl bg-[#F7F5F2] p-5 flex gap-4">
                  <div className="shrink-0 w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: `${g.color}18` }}>
                    <g.icon className="w-5 h-5" style={{ color: g.color }} strokeWidth={2.25} />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-[#1B2A4A]">{g.title}</p>
                    <p className="text-sm text-[#1B2A4A]/75 mt-1 leading-relaxed">{g.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-10">
            <h2 className="font-heading text-lg font-bold uppercase tracking-widest text-[#E8621A]">
              What makes Surviving Together, Surviving Together
            </h2>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {APP_SPECIFIC.map((g, i) => (
                <div key={g.title} data-testid={`guideline-app-${i + 1}`} className="rounded-2xl bg-white border border-[#E8621A]/20 p-5 flex gap-4">
                  <div className="shrink-0 w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: `${g.color}18` }}>
                    <g.icon className="w-5 h-5" style={{ color: g.color }} strokeWidth={2.25} />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-[#1B2A4A]">{g.title}</p>
                    <p className="text-sm text-[#1B2A4A]/75 mt-1 leading-relaxed">{g.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <div className="mt-10 rounded-2xl bg-[#1B2A4A] text-white p-6 sm:p-7">
            <p className="font-heading font-bold text-lg">Three strikes, and rest.</p>
            <p className="mt-2 text-sm text-white/80 leading-relaxed">
              If a post breaks these guidelines, our team will remove it and send a friendly warning.
              After three warnings, the account is paused so the community can stay a safe place to land.
              We are always here to talk if something feels wrong — reach out any time.
            </p>
          </div>

          <p className="mt-8 text-sm text-[#1B2A4A]/60 leading-relaxed">
            Thank you for helping make Surviving Together a place where neighbors truly help neighbors. 💛
          </p>
        </div>
      </Wrap>
    </div>
  );
}
